package com.evoapps.evoshift.data.mapper

import com.evoapps.evoshift.data.dto.workplace.WorkplacePayrollDto
import com.evoapps.evoshift.domain.market.Market
import com.evoapps.evoshift.domain.market.payroll.WorkplacePayrollSettings
import com.evoapps.evoshift.data.dto.workplace.WorkplaceSettingsDto
import com.evoapps.evoshift.domain.model.SundayPolicy
import com.evoapps.evoshift.domain.model.GeofenceMode
import com.evoapps.evoshift.domain.model.GeofenceSettings
import com.evoapps.evoshift.data.dto.workplace.GeofenceDto
import com.evoapps.evoshift.data.dto.workplace.BreakPolicyDto
import com.evoapps.evoshift.domain.model.BreakPolicy
import com.evoapps.evoshift.domain.model.ClockReviewSettings
import com.evoapps.evoshift.data.dto.workplace.ClockReviewSettingsDto
import com.evoapps.evoshift.domain.model.SwapPolicy
import com.evoapps.evoshift.domain.model.WorkplaceSettings
import kotlinx.datetime.todayIn

class WorkplaceSettingsMapper(
    private val availabilitySlotMapper: AvailabilitySlotMapper,
    /** SH-232: today ("YYYY-MM-DD") in a time zone, for the periods without pay as they stand. */
    private val today: (String) -> String = { zone ->
        val tz = runCatching { kotlinx.datetime.TimeZone.of(zone) }.getOrElse { kotlinx.datetime.TimeZone.currentSystemDefault() }
        com.evoapps.evoshift.domain.util.AppClock.todayIn(tz).toString()
    }
) {
    /** SH-232: the stored periods without pay, older app versions' changes to the list taken in (UnpaidWork.reconcile). */
    private fun unpaidPeriodsOf(dto: WorkplaceSettingsDto): Map<String, List<com.evoapps.evoshift.domain.budget.UnpaidPeriod>> =
        com.evoapps.evoshift.domain.budget.UnpaidWork.reconcile(
            periods = dto.unpaidPeriods.mapValues { (_, list) -> list.map { com.evoapps.evoshift.domain.budget.UnpaidPeriod(it.from, it.until) } },
            legacy = dto.payroll.unpaidUserIds,
            asOf = dto.unpaidPeriodsAsOf,
            today = today(dto.timezone)
        )

    /** SH-232: [periods] as stored. */
    fun unpaidPeriodsDto(periods: Map<String, List<com.evoapps.evoshift.domain.budget.UnpaidPeriod>>): Map<String, List<com.evoapps.evoshift.data.dto.workplace.UnpaidPeriodDto>> =
        periods.mapValues { (_, list) -> list.map { com.evoapps.evoshift.data.dto.workplace.UnpaidPeriodDto(it.from, it.until) } }

    fun toDomain(dto: WorkplaceSettingsDto): WorkplaceSettings {
        return WorkplaceSettings(
            timezone = dto.timezone,
            currency = dto.currency,
            firstDayOfWeek = dto.firstDayOfWeek,
            availableRoles = dto.availableRoles,
            shiftSwapPolicy = try {
                SwapPolicy.valueOf(dto.shiftSwapPolicy)
            } catch (e: Exception) {
                SwapPolicy.A_DIRECT // Default fallback
            },
            defaultWeeklySkeleton = dto.defaultWeeklySkeleton,
            definedAvailabilitySlots = dto.definedAvailabilitySlots.map {
                availabilitySlotMapper.toDomain(
                    it
                )
            },
            workplaceSkills = dto.workplaceSkills,
            leadRoles = dto.leadRoles,
            leadSkills = dto.leadSkills,
            market = Market.fromId(dto.market),
            payroll = WorkplacePayrollSettings(
                dto.payroll.accidentInsuranceRate, dto.payroll.nightStart, dto.payroll.nightEnd, dto.payroll.ownerShiftsUnpaid,
                settlementPeriodMonths = dto.payroll.settlementPeriodMonths.coerceIn(1, 4),
                overtimeTimeOffInLieu = dto.payroll.overtimeTimeOffInLieu,
                unpaidUserIds = dto.payroll.unpaidUserIds.distinct(),
                unpaidPeriods = unpaidPeriodsOf(dto),
                openShiftCost = com.evoapps.evoshift.domain.market.payroll.OpenShiftCostSetting(
                    com.evoapps.evoshift.domain.market.payroll.OpenShiftCostMode.fromId(dto.payroll.openShiftCostMode),
                    dto.payroll.openShiftCostPerHour
                )
            ),
            noShowAlertMinutes = dto.noShowAlertMinutes,
            checklistAlertMinutes = dto.checklistAlertMinutes,
            blockExpiredStaffChecks = dto.blockExpiredStaffChecks,
            restRulesForAllContracts = dto.restRulesForAllContracts,
            sundayPolicy = SundayPolicy.fromName(dto.sundayPolicy),
            geofence = dto.geofence?.let {
                GeofenceSettings(GeofenceMode.fromName(it.mode), GeofenceSettings.radiusOrDefault(it.radiusMeters))
            } ?: GeofenceSettings(),
            breakPolicy = dto.breakPolicy?.let { BreakPolicy.of(it.paidBreak, it.unpaidBreakMinutes, it.reminderAfterHours) } ?: BreakPolicy(),
            clockReview = dto.clockReview?.let {
                ClockReviewSettings.of(
                    it.earlyInMinutes, it.lateOutMinutes, it.maxOverrunMinutes, it.veryShortMinutes, it.countFromPlannedStart,
                    it.kioskQrOnly, it.kioskPhoto, it.sameTabletSeconds, it.flagNoAppActivity
                )
            } ?: ClockReviewSettings(),
            weatherEnabled = dto.weatherEnabled,
            specialDaysEnabled = dto.specialDaysEnabled,
            voivodeship = com.evoapps.evoshift.domain.model.Voivodeship.fromName(dto.voivodeship),
            showEvoShiftFooter = dto.showEvoShiftFooter,
            workHours = dto.workHours?.let { com.evoapps.evoshift.domain.model.WorkHoursSettings.of(it.selfEntry) }
                ?: com.evoapps.evoshift.domain.model.WorkHoursSettings(),
            reports = reportsOf(dto.reports),
            rosterVisibility = com.evoapps.evoshift.domain.model.RosterVisibility.fromName(dto.rosterVisibility),
            rosterHasHiddenPeople = dto.rosterHasHiddenPeople,
            requests = dto.requests?.let {
                com.evoapps.evoshift.domain.model.RequestSettings(
                    it.bloodDonationDays, it.childcareDays, it.childcareHours, it.forceMajeureDays, it.forceMajeureHours,
                    it.carerLeaveDays, it.careAllowanceChildDays, it.careAllowanceFamilyDays, it.careAllowanceTotalDays,
                    types = it.types.mapValues { (_, rule) ->
                        com.evoapps.evoshift.domain.model.RequestTypeRule(
                            rule.enabled,
                            rule.contracts?.mapNotNull { name -> com.evoapps.evoshift.domain.model.SystemContractType.entries.firstOrNull { c -> c.name == name } }?.toSet(),
                            rule.contractTypeIds.orEmpty().filter { id -> id.isNotBlank() }.toSet()
                        )
                    },
                    defaults = if (it.defaults == com.evoapps.evoshift.domain.model.RequestDefaults.RECOMMENDED.name)
                        com.evoapps.evoshift.domain.model.RequestDefaults.RECOMMENDED else com.evoapps.evoshift.domain.model.RequestDefaults.LEGACY
                ).clamped()
            } ?: com.evoapps.evoshift.domain.model.RequestSettings.LEGACY
        )
    }

    fun toDto(domain: WorkplaceSettings): WorkplaceSettingsDto {
        return WorkplaceSettingsDto(
            timezone = domain.timezone,
            currency = domain.currency,
            firstDayOfWeek = domain.firstDayOfWeek,
            availableRoles = domain.availableRoles,
            workplaceSkills = domain.workplaceSkills,
            leadRoles = domain.leadRoles,
            leadSkills = domain.leadSkills,
            shiftSwapPolicy = domain.shiftSwapPolicy.name,
            defaultWeeklySkeleton = domain.defaultWeeklySkeleton,
            definedAvailabilitySlots = domain.definedAvailabilitySlots.map {
                availabilitySlotMapper.toDto(
                    it
                )
            },
            market = domain.market.id,
            payroll = WorkplacePayrollDto(
                domain.payroll.accidentInsuranceRate, domain.payroll.nightStart, domain.payroll.nightEnd, domain.payroll.ownerShiftsUnpaid,
                domain.payroll.settlementPeriodMonths, domain.payroll.overtimeTimeOffInLieu,
                unpaidUserIds = domain.payroll.unpaidUserIds.distinct(),
                openShiftCostMode = domain.payroll.openShiftCost.mode.name,
                openShiftCostPerHour = domain.payroll.openShiftCost.validPerHour
            ),
            noShowAlertMinutes = domain.noShowAlertMinutes,
            checklistAlertMinutes = domain.checklistAlertMinutes,
            blockExpiredStaffChecks = domain.blockExpiredStaffChecks,
            restRulesForAllContracts = domain.restRulesForAllContracts,
            sundayPolicy = domain.sundayPolicy.name,
            geofence = GeofenceDto(domain.geofence.mode.name, domain.geofence.radiusMeters),
            breakPolicy = BreakPolicyDto(domain.breakPolicy.paidBreak, domain.breakPolicy.unpaidBreakMinutes, domain.breakPolicy.reminderAfterHours),
            clockReview = domain.clockReview.let {
                ClockReviewSettingsDto(
                    it.earlyInMinutes, it.lateOutMinutes, it.maxOverrunMinutes, it.veryShortMinutes, it.countFromPlannedStart,
                    it.kioskQrOnly, it.kioskPhoto, it.sameTabletSeconds, it.flagNoAppActivity
                )
            },
            weatherEnabled = domain.weatherEnabled,
            specialDaysEnabled = domain.specialDaysEnabled,
            voivodeship = domain.voivodeship?.name,
            requests = requestsDto(domain.requests),
            showEvoShiftFooter = domain.showEvoShiftFooter,
            workHours = com.evoapps.evoshift.data.dto.workplace.WorkHoursSettingsDto(domain.workHours.selfEntry),
            reports = reportsDto(domain.reports),
            unpaidPeriods = unpaidPeriodsDto(domain.payroll.unpaidPeriods),
            rosterVisibility = domain.rosterVisibility.name,
            rosterHasHiddenPeople = domain.rosterHasHiddenPeople
        )
    }

    /** SH-258: `settings.reports` read tolerantly (unknown values: the defaults). */
    fun reportsOf(dto: com.evoapps.evoshift.data.dto.workplace.ReportSettingsDto?): com.evoapps.evoshift.domain.model.ReportSettings {
        val t = dto?.timesheet ?: return com.evoapps.evoshift.domain.model.ReportSettings()
        return com.evoapps.evoshift.domain.model.ReportSettings(
            com.evoapps.evoshift.domain.model.TimesheetReportSettings.of(
                com.evoapps.evoshift.domain.model.TimesheetHoursSource.fromName(t.hoursSource),
                t.roundingMinutes,
                com.evoapps.evoshift.domain.model.TimeRoundingMode.fromName(t.roundingMode),
                t.showRemarks,
                t.showManagerSignature,
                com.evoapps.evoshift.domain.model.WeekdayFormat.fromName(t.weekday)
            )
        )
    }

    /** SH-258: `settings.reports` as stored. */
    fun reportsDto(reports: com.evoapps.evoshift.domain.model.ReportSettings) = reports.timesheet.let {
        com.evoapps.evoshift.data.dto.workplace.ReportSettingsDto(
            com.evoapps.evoshift.data.dto.workplace.TimesheetReportSettingsDto(
                it.hoursSource.name, it.roundingMinutes, it.roundingMode.name, it.showRemarks, it.showManagerSignature, it.weekday.name
            )
        )
    }

    /** SH-223: `settings.requests` as stored. */
    fun requestsDto(requests: com.evoapps.evoshift.domain.model.RequestSettings) = requests.clamped().let {
        com.evoapps.evoshift.data.dto.workplace.RequestSettingsDto(
            it.bloodDonationDays, it.childcareDays, it.childcareHours, it.forceMajeureDays, it.forceMajeureHours,
            it.carerLeaveDays, it.careAllowanceChildDays, it.careAllowanceFamilyDays, it.careAllowanceTotalDays,
            types = it.types.mapValues { (_, rule) ->
                com.evoapps.evoshift.data.dto.workplace.RequestTypeRuleDto(
                    rule.enabled, rule.contracts?.map { c -> c.name }?.sorted(),
                    rule.contractTypeIds.sorted().takeIf { ids -> ids.isNotEmpty() }
                )
            },
            defaults = it.defaults.name
        )
    }
}
